var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { useMemo, useCallback, useState, useEffect } from 'react';
import { useStyles } from './CreditCard.styles';
import { Typography, Button, Box } from '@mui/material';
import Mastercard from './logos/mastercard.svg';
import Visa from './logos/visa.svg';
import { useApi } from '../../Context/APIContext';
import useApiAbort from '../../Api/Core/useApiAbort';
import Wrapper from '../Wrapper/Wrapper';
import { useSnackbar } from 'notistack';
import { DibsIframeWrapper } from '../DibsIframeWrapper/DibsIframeWrapper';
const getPaymentId = localStorage['change-payment-method-payment-id'] || undefined;
const renderExpiryDate = (expiry) => {
    return `${expiry.substring(0, 2)}/${expiry.substring(2, 4)}`;
};
export default function CreditCard() {
    const classes = useStyles();
    const api = useApi();
    const abortController = useApiAbort();
    if (api) {
        api.setAbortController(abortController.current);
    }
    const [paymentId, setPaymentId] = useState(getPaymentId);
    const [paymentMethod, setPaymentMethod] = useState(undefined);
    const [isNewPaymentId, setIsNewPaymentId] = useState(false);
    const fetchPaymentMethod = useCallback(() => __awaiter(this, void 0, void 0, function* () {
        if (api) {
            try {
                const method = yield api.getPaymentMethod();
                if (abortController.current.signal.aborted) {
                    return;
                }
                setPaymentMethod(method);
            }
            catch (_) { }
        }
    }), [api, abortController]);
    useEffect(() => {
        fetchPaymentMethod();
    }, [fetchPaymentMethod]);
    const { enqueueSnackbar } = useSnackbar();
    const changeCard = () => __awaiter(this, void 0, void 0, function* () {
        if (api) {
            const result = yield api.initChangePaymentMethod();
            localStorage['change-payment-method-payment-id'] = result.paymentId;
            setPaymentId(result.paymentId);
            setIsNewPaymentId(true);
        }
    });
    const isVisa = useMemo(() => {
        return paymentMethod && paymentMethod.cardType.toLowerCase() === 'visa';
    }, [paymentMethod]);
    const isMastercard = useMemo(() => {
        return paymentMethod && paymentMethod.cardType.toLowerCase() === 'mastercard';
    }, [paymentMethod]);
    const getBrand = useCallback(() => {
        if (paymentMethod === undefined)
            return React.createElement(React.Fragment, null);
        if (isMastercard) {
            return React.createElement("img", { src: Mastercard, alt: "Mastercard" });
        }
        if (isVisa) {
            return React.createElement("img", { src: Visa, alt: "Visa" });
        }
        return React.createElement(Typography, { sx: classes.otherbrand }, paymentMethod.cardType);
    }, [isVisa, isMastercard, classes.otherbrand, paymentMethod]);
    const handleCompleted = () => {
        localStorage.removeItem('change-payment-method-payment-id');
        setPaymentId(undefined);
        enqueueSnackbar('Din betalningsmetod är nu ändrad');
        setTimeout(fetchPaymentMethod, 2000);
    };
    const handleAbort = () => {
        localStorage.removeItem('change-payment-method-payment-id');
        setPaymentId(undefined);
        setIsNewPaymentId(false);
    };
    const styles = [
        classes.root,
        ...(isVisa ? [classes.visa] : []),
        ...(isMastercard ? [classes.mastercard] : []),
        ...(!isVisa && !isMastercard ? [classes.other] : []),
    ];
    if (!paymentId && paymentMethod) {
        return (React.createElement(React.Fragment, null,
            React.createElement(Typography, { variant: "subtitle1" }, "Nuvarande kortuppgifter:"),
            React.createElement(Box, { component: "div", sx: styles },
                React.createElement(Box, { component: "header", sx: classes.header },
                    React.createElement(Box, { component: "div", sx: classes.brand }, getBrand()),
                    React.createElement(Box, { component: "div" },
                        React.createElement(Button, { onClick: changeCard, sx: classes.changeCard }, "Byt kort"))),
                React.createElement(Box, { component: "div", sx: classes.info },
                    React.createElement(Typography, { sx: classes.number }, paymentMethod.maskedNumber),
                    React.createElement(Typography, null, renderExpiryDate(paymentMethod.expiryDate))))));
    }
    if (paymentId && paymentMethod && isNewPaymentId) {
        return (React.createElement(Wrapper, null,
            React.createElement(DibsIframeWrapper, { onCompleted: handleCompleted, paymentId: paymentId }),
            React.createElement(Button, { onClick: handleAbort }, "Avbryt")));
    }
    return React.createElement(React.Fragment, null);
}
